import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, contactAttributes, fieldOptions, formatIst, inboxApi, PERMISSION, useAuth, type Contact, type ContactField, type Segment } from '@wa/shared';
import { useAway } from '@/customers/FilterBuilder';

/** How the opt-out happened, in words an agent can act on. */
const OPT_OUT_REASON: Record<string, string> = {
  keyword: 'replied STOP',
  button: 'tapped Stop promotions',
  whatsapp: 'blocked marketing in WhatsApp',
  agent: 'set by an agent',
  import: 'from an import',
};

/**
 * The reference's "Customer Details" panel: the person at the top with a pencil,
 * the opt-in tick, the broadcast lists they are on, then the custom fields —
 * added one at a time from a picker, because a form of twelve empty boxes is
 * a form nobody fills in.
 *
 * Opting somebody back in asks first. An agent's tick is not the customer's
 * consent, and the confirmation says so; a customer who writes in again does
 * not renew consent either (F22).
 */
export function CustomerDetails({ contact, fields, segments, onClose, onChanged }: {
  contact: Contact; fields: ContactField[]; segments: Segment[]; onClose: () => void; onChanged: () => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canWrite = can(user, PERMISSION.contactsWrite);
  const canLists = can(user, PERMISSION.segmentsWrite);
  const [rename, setRename] = useState(false);
  const [confirmOptIn, setConfirmOptIn] = useState(false);
  const [lists, setLists] = useState(false);
  const [adding, setAdding] = useState<ContactField | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setRename(false); setConfirmOptIn(false); setLists(false); setAdding(null); setError(null); }, [contact.id]);

  const membership = useQuery({ queryKey: ['contact-segments', contact.id], queryFn: () => inboxApi.contactSegments(contact.id) });
  const values = contactAttributes(contact);
  const set = fields.filter((f) => (values[f.fieldKey] ?? '') !== '');
  const unset = fields.filter((f) => (values[f.fieldKey] ?? '') === '');

  const refresh = () => { onChanged(); void queryClient.invalidateQueries({ queryKey: ['contact-segments', contact.id] }); void queryClient.invalidateQueries({ queryKey: ['segments'] }); };
  const fail = (e: unknown, fallback: string) => setError(e instanceof Error ? e.message : fallback);

  const optOut = useMutation({ mutationFn: () => inboxApi.optOut(contact.id), onSuccess: refresh, onError: (e) => fail(e, 'Could not opt them out.') });
  const optIn = useMutation({ mutationFn: () => inboxApi.optIn(contact.id), onSuccess: () => { setConfirmOptIn(false); refresh(); }, onError: (e) => fail(e, 'Could not opt them back in.') });
  const setField = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string | null }) => inboxApi.updateContact(contact.id, { attributes: { [key]: value } }),
    onSuccess: () => { setAdding(null); refresh(); }, onError: (e) => fail(e, 'Could not save that.'),
  });
  const toggleList = useMutation({
    mutationFn: async ({ id, on }: { id: number; on: boolean }) => { if (on) await inboxApi.addSegmentMembers(id, [contact.id]); else await inboxApi.removeSegmentMember(id, contact.id); },
    onSuccess: refresh, onError: (e) => fail(e, 'Could not change that list.'),
  });

  const optedIn = contact.optedOutAt === null;
  const initial = (contact.displayName ?? contact.waId).trim().charAt(0).toUpperCase();

  return (
    <aside className="flex h-full min-h-0 flex-col bg-(--color-surface)" aria-label="Customer details">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-(--color-line) px-4">
        <h2 className="text-lead font-semibold">Customer Details</h2>
        <button type="button" onClick={onClose} aria-label="Close" className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)">
          <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg>
        </button>
      </header>

      <div className="pane-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="flex items-center gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-(--color-tint-2) text-lead font-semibold text-(--color-tint-ink-2)">{initial}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-lead font-semibold">{contact.displayName ?? contact.waId}</span>
            <span className="block text-body text-(--color-muted)">+{contact.waId}</span>
          </span>
          {canWrite && (
            <button type="button" onClick={() => setRename(true)} aria-label="Edit name" className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)">
              <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><path d="M4 16h3l8-8-3-3-8 8zM11 6l3 3" {...s} /></svg>
            </button>
          )}
        </div>

        {contact.blockedAt && (
          <p className="mt-3 rounded-(--radius-field) bg-(--color-bad)/8 px-3 py-2 text-small text-(--color-bad)">Blocked {formatIst(contact.blockedAt)}. Nothing is sent to them.</p>
        )}

        <label className="mt-4 flex items-center gap-2.5 border-t border-(--color-line-dim) pt-4">
          <input
            type="checkbox" checked={optedIn} disabled={!canWrite || optOut.isPending || optIn.isPending}
            onChange={(e) => { if (e.target.checked) setConfirmOptIn(true); else optOut.mutate(); }}
            className="size-4.5 accent-(--color-brand)"
          />
          <span className="text-body font-medium">Opt-in</span>
          <span title="Whether this person may receive broadcasts and templates. A customer who replies STOP is opted out automatically; opting them back in is your responsibility to justify." className="ml-auto text-(--color-faint)">
            <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><circle cx="10" cy="10" r="7" {...s} /><path d="M10 9v4.5M10 6.5v.2" {...s} /></svg>
          </span>
        </label>
        {!optedIn && (
          <p className="mt-1 text-small text-(--color-muted)">
            Opted out {formatIst(contact.optedOutAt)}{contact.optOutReason ? ` · ${OPT_OUT_REASON[contact.optOutReason] ?? contact.optOutReason}` : ''}{contact.optOutBy ? ` · by ${contact.optOutBy}` : ''}.
          </p>
        )}
        {confirmOptIn && (
          <div className="mt-2 rounded-(--radius-field) border border-(--color-note-line) bg-(--color-note) px-3 py-2.5">
            <p className="text-small text-(--color-note-ink)">Only opt them back in if they asked to be. A tick here is your word, not theirs, and a complaint to WhatsApp counts against the account.</p>
            <div className="mt-2 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmOptIn(false)} className="tap rounded-(--radius-field) px-2.5 py-1 text-small font-medium text-(--color-note-ink) hover:bg-(--color-surface)/60">Cancel</button>
              <button type="button" disabled={optIn.isPending} onClick={() => optIn.mutate()} className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-1 text-small font-semibold text-(--color-brand-ink) disabled:opacity-60">They asked — opt in</button>
            </div>
          </div>
        )}

        <div className="mt-4 border-t border-(--color-line-dim) pt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-micro font-semibold tracking-wide text-(--color-muted) uppercase">Belonging to broadcast list</h3>
            {canLists && (
              <button type="button" onClick={() => setLists(true)} aria-label="Choose lists" className="tap grid size-8 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)">
                <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><path d="M3 6h9M15 6h2M3 10h3M9 10h8M3 14h11M17 14h0" {...s} /><circle cx="13" cy="6" r="1.6" {...s} /><circle cx="7" cy="10" r="1.6" {...s} /><circle cx="15.5" cy="14" r="1.6" {...s} /></svg>
              </button>
            )}
          </div>
          {membership.isPending && <p className="mt-1 text-small text-(--color-muted)">Loading…</p>}
          {membership.data?.length === 0 && <p className="mt-1 text-body text-(--color-faint)">Not on any list.</p>}
          <ul className="mt-1 flex flex-col gap-1">
            {(membership.data ?? []).map((m) => (
              <li key={m.id} className="flex items-center gap-1.5 text-body">
                {m.name}{m.kind === 1 && <IconBolt />}
              </li>
            ))}
          </ul>
        </div>

        {set.length > 0 && (
          <dl className="mt-4 flex flex-col gap-3 border-t border-(--color-line-dim) pt-4">
            {set.map((f) => (
              <FieldRow key={f.id} field={f} value={values[f.fieldKey] ?? ''} canWrite={canWrite} busy={setField.isPending}
                onSave={(v) => setField.mutate({ key: f.fieldKey, value: v === '' ? null : v })} />
            ))}
          </dl>
        )}

        {error && <p role="alert" className="mt-3 text-small text-(--color-bad)">{error}</p>}
      </div>

      {canWrite && (
        <footer className="relative shrink-0 border-t border-(--color-line-dim) px-4 py-3 text-center">
          {adding ? (
            <FieldEditor field={adding} value="" busy={setField.isPending} onCancel={() => setAdding(null)}
              onSave={(v) => setField.mutate({ key: adding.fieldKey, value: v === '' ? null : v })} />
          ) : (
            <AddFieldPicker fields={unset} onPick={setAdding} />
          )}
        </footer>
      )}

      {rename && <RenameDialog contact={contact} onClose={() => setRename(false)} onSaved={() => { setRename(false); refresh(); }} />}
      {lists && (
        <ListPicker
          segments={segments.filter((sg) => sg.kind === 0)} on={new Set((membership.data ?? []).map((m) => m.id))}
          busy={toggleList.isPending} onToggle={(id, on) => toggleList.mutate({ id, on })} onClose={() => setLists(false)}
        />
      )}
    </aside>
  );
}

function FieldRow({ field, value, canWrite, busy, onSave }: { field: ContactField; value: string; canWrite: boolean; busy: boolean; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  if (editing) return <FieldEditor field={field} value={value} busy={busy} onCancel={() => setEditing(false)} onSave={(v) => { onSave(v); setEditing(false); }} />;
  return (
    <div className="group flex items-start gap-2">
      <span className="min-w-0 flex-1">
        <dt className="text-small text-(--color-muted)">{field.label}</dt>
        <dd className="text-body break-words">{field.fieldType === 'boolean' ? (value === 'true' ? 'Yes' : 'No') : value}</dd>
      </span>
      {canWrite && (
        <button type="button" onClick={() => setEditing(true)} aria-label={`Edit ${field.label}`} className="tap grid size-8 place-items-center rounded-full text-(--color-faint) hover:bg-(--color-sunken) hover:text-(--color-ink)">
          <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 16h3l8-8-3-3-8 8zM11 6l3 3" {...s} /></svg>
        </button>
      )}
    </div>
  );
}

function FieldEditor({ field, value, busy, onCancel, onSave }: { field: ContactField; value: string; busy: boolean; onCancel: () => void; onSave: (v: string) => void }) {
  const [v, setV] = useState(value);
  const input = 'tap w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-3 py-2 text-body outline-none focus:border-(--color-brand)';
  const options = field.fieldType === 'list' ? fieldOptions(field) : [];
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(v.trim()); }} className="text-left">
      <label className="block text-small text-(--color-muted)">{field.label}</label>
      {field.fieldType === 'list' ? (
        <select autoFocus value={v} onChange={(e) => setV(e.target.value)} className={`mt-1 ${input}`}>
          <option value="">—</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.fieldType === 'boolean' ? (
        <select autoFocus value={v} onChange={(e) => setV(e.target.value)} className={`mt-1 ${input}`}>
          <option value="">—</option><option value="true">Yes</option><option value="false">No</option>
        </select>
      ) : (
        <input autoFocus type={field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : 'text'} value={v} onChange={(e) => setV(e.target.value)} className={`mt-1 ${input}`} />
      )}
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="tap rounded-(--radius-field) px-2.5 py-1 text-small font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
        <button type="submit" disabled={busy} className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-1 text-small font-semibold text-(--color-brand-ink) disabled:opacity-60">Save</button>
      </div>
    </form>
  );
}

function AddFieldPicker({ fields, onPick }: { fields: ContactField[]; onPick: (f: ContactField) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);
  useAway(box, open, () => setOpen(false));
  const shown = fields.filter((f) => f.label.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div ref={box} className="relative">
      <button type="button" onClick={() => { setOpen((o) => !o); setQ(''); }} disabled={fields.length === 0} className="tap text-body font-medium text-(--color-brand) hover:underline disabled:opacity-50">
        {fields.length === 0 ? 'Every field is filled in' : 'Add custom field'}
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-20 mb-2 w-60 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) py-1 text-left shadow-(--shadow-pop)">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search custom field" aria-label="Search fields" className="mx-2 mb-1 w-[calc(100%-1rem)] border-b border-(--color-line) bg-transparent px-1 py-1.5 text-body outline-none placeholder:text-(--color-faint)" />
          <ul className="max-h-56 overflow-y-auto">
            {shown.map((f) => <li key={f.id}><button type="button" onClick={() => { onPick(f); setOpen(false); }} className="tap block w-full px-3 py-1.5 text-left text-body hover:bg-(--color-sunken)">{f.label}</button></li>)}
            {shown.length === 0 && <li className="px-3 py-2 text-small text-(--color-muted)">Nothing matches.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

/** The reference's "Manage personal information": the name, and only the name — the number is WhatsApp's. */
function RenameDialog({ contact, onClose, onSaved }: { contact: Contact; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(contact.displayName ?? '');
  const save = useMutation({ mutationFn: () => inboxApi.updateContact(contact.id, { displayName: name.trim() }), onSuccess: onSaved });
  const initial = (name || contact.waId).trim().charAt(0).toUpperCase();
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <form role="dialog" aria-label="Manage personal information" onSubmit={(e) => { e.preventDefault(); save.mutate(); }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xs rounded-(--radius-panel) bg-(--color-surface) shadow-(--shadow-pop)">
        <header className="flex items-center justify-between border-b border-(--color-line) px-4 py-3">
          <h3 className="text-lead font-semibold">Manage personal information</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="tap grid size-8 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)"><svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg></button>
        </header>
        <div className="px-4 py-4">
          <span className="mx-auto grid size-12 place-items-center rounded-full bg-(--color-tint-2) text-lead font-semibold text-(--color-tint-ink-2)">{initial}</span>
          <label className="mt-4 block text-small text-(--color-muted)">Customer name</label>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} className="tap mt-1 w-full rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-body outline-none focus:border-(--color-brand)" />
          {save.isError && <p role="alert" className="mt-2 text-small text-(--color-bad)">{save.error instanceof Error ? save.error.message : 'Could not save.'}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="tap rounded-(--radius-field) px-3 py-2 text-small font-semibold tracking-wide text-(--color-muted) uppercase hover:bg-(--color-sunken)">Cancel</button>
            <button type="submit" disabled={save.isPending} className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-small font-semibold tracking-wide text-(--color-brand-ink) uppercase disabled:opacity-60">Save</button>
          </div>
        </div>
      </form>
    </div>
  );
}

/** "Select one or more lists" — static lists only; a rule-based list decides its own members. */
export function ListPicker({ segments, on, busy, onToggle, onClose, title = 'Select one or more lists' }: {
  segments: Segment[]; on: Set<number>; busy: boolean; onToggle: (id: number, on: boolean) => void; onClose: () => void; title?: string;
}) {
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const shown = segments.filter((sg) => sg.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()} className="flex max-h-[80vh] w-full max-w-xs flex-col rounded-(--radius-panel) bg-(--color-surface) shadow-(--shadow-pop)">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-(--color-line) px-3">
          <button type="button" onClick={onClose} aria-label="Back" className="tap grid size-8 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)"><svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M16 10H4M9 4.5 3.5 10 9 15.5" {...s} strokeWidth={1.8} /></svg></button>
          {searching
            ? <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lists" aria-label="Search lists" className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)" />
            : <h3 className="flex-1 text-body font-semibold">{title}</h3>}
          <button type="button" onClick={() => { setSearching((x) => !x); setQ(''); }} aria-label="Search" className="tap grid size-8 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)"><svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><circle cx="8.8" cy="8.8" r="5.2" {...s} /><path d="m12.8 12.8 3.6 3.6" {...s} /></svg></button>
        </header>
        <ul className="pane-scroll min-h-0 flex-1 overflow-y-auto px-3 py-1">
          {shown.length === 0 && <li className="py-6 text-center text-small text-(--color-muted)">{segments.length === 0 ? 'No static lists yet. Rule-based lists choose their own members.' : 'Nothing matches.'}</li>}
          {shown.map((sg) => (
            <li key={sg.id} className="border-b border-(--color-line-dim) last:border-b-0">
              <label className="flex cursor-pointer items-center gap-3 py-2.5">
                <input type="checkbox" checked={on.has(sg.id)} disabled={busy} onChange={(e) => onToggle(sg.id, e.target.checked)} className="size-4.5 accent-(--color-brand)" />
                <span className="text-body">{sg.name}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
export const IconBolt = () => <svg viewBox="0 0 20 20" className="size-3.5 text-(--color-brand)" aria-hidden="true"><path d="M11.5 2.5 5 11h4.2L8.5 17.5 15 9h-4.2z" fill="currentColor" /></svg>;
